import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

interface CommonProps {
	variant?: ButtonVariant;
	size?: ButtonSize;
	children?: ReactNode;
	className?: string;
}

const base =
	"relative inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-control)] " +
	"text-sm font-medium tracking-tight select-none " +
	"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 " +
	"disabled:pointer-events-none disabled:opacity-40";

const variants: Record<ButtonVariant, string> = {
	primary:
		"bg-teal-500 text-zinc-950 hover:bg-teal-400 active:bg-teal-600 " +
		"shadow-[0_1px_2px_rgb(0_0_0/0.12)]",
	secondary:
		"bg-zinc-950/[0.06] text-fg hover:bg-zinc-950/[0.1] active:bg-zinc-950/[0.12] " +
		"dark:bg-white/10 dark:hover:bg-white/[0.14] dark:active:bg-white/[0.18]",
	ghost:
		"text-fg-muted hover:bg-zinc-950/[0.06] hover:text-fg " +
		"dark:hover:bg-white/10",
	danger:
		"bg-rose-500/[0.08] text-rose-700 hover:bg-rose-500/[0.14] " +
		"dark:text-rose-300 dark:hover:bg-rose-500/20",
};

const sizes: Record<ButtonSize, string> = {
	sm: "h-8 min-h-8 px-2.5 gap-1",
	md: "h-9 min-h-9 px-3",
	lg: "h-9 min-h-9 px-3.5",
};

type ButtonAsButton = CommonProps &
	Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
		as?: "button";
	};

type ButtonAsAnchor = CommonProps &
	Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> & {
		as: "a";
		href: string;
	};

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

export function Button(props: ButtonProps) {
	const { variant = "primary", size = "md", className, children, ...rest } = props;
	const cls = cn(base, variants[variant], sizes[size], className);

	if (props.as === "a") {
		const { as: _as, ...anchorProps } = rest as typeof rest & { as?: "a" };
		return (
			<a className={cls} {...(anchorProps as AnchorHTMLAttributes<HTMLAnchorElement>)}>
				{children}
			</a>
		);
	}

	const { as: _as, ...buttonProps } = rest as typeof rest & { as?: undefined };
	return (
		<button className={cls} {...(buttonProps as ButtonHTMLAttributes<HTMLButtonElement>)}>
			{children}
		</button>
	);
}
